// @ds/connected/sharedSections — reusable section factories.
//
// Defined ONCE here, every entity imports + uses instead of repeating the
// same custom React across 10 entity configs. Each factory returns a
// `SectionDef<T>` ready to drop into `ViewConfig.sections[]`.
//
// Per the Pass G1 / Phase F resolution: NO `useRun*Query` in `src/entities/**`.
// Every factory mounts an internal child component that owns the fetch via
// `useRunListQuery`, so the entity layer stays declarative.
//
// Four factories:
//
//   1. activitySection<T>(commentableType, opts?)
//        CustomSection wrapping `<ActivityLog>`. Fetches against the shared
//        `auditLogSchema` filtered by `entity_type` + `entity_id`. Reused by
//        invoices / transactions / edpr / ...
//
//   2. commentsSection<T>(commentableType, opts?)
//        CustomSection wrapping `<CommentThread>` (the connected primitive).
//        The connected primitive already owns its fetch — this factory is a
//        thin pass-through that picks `record.id` for `commentableId`.
//
//   3. checklistSection<T>(opts)
//        CustomSection rendering a Stack of `<ChecklistRow>` steps. Steps are
//        built per-record via `steps(record, t)`. Reused by every entity
//        with a status state machine (invoice doc checklist, edpr documents
//        checklist, transaction completion checklist).
//
//   4. filesSection<T, TFile>(opts)
//        CustomSection that fetches a file collection scoped by `parentFk`
//        and renders one bucket-row per `FileBucket`. Each bucket row is a
//        label + `<FileListPills>` pair (existing primitive). Reused by
//        transactions / invoices / edpr / ...

import type { TFunction } from "i18next";
import type { FileListPillsItem } from "@ds/tables/cells/FileListPills";

import type { Schema } from "@ds/config/schema";

import type { CustomSection, SectionDef } from "@ds/config/sectionConfig";

import type { AnyAuditChildScope } from "@ds/data/auditChildScope";

import { Stack } from "@ds/layout/Stack";


import {
  ChecklistRow,
  type ChecklistRowStatus,
} from "@ds/connected/ChecklistRow";

import {
  ActivityBody,
  CommentsBody,
  FilesBody,
} from "@ds/connected/sharedSections.Bodies";

// ─── Shared types ──────────────────────────────────────────────────────────

/** Step shape for the `checklistSection()` factory. */
export interface ChecklistStep {
  /** Stable id used for testids (`checklist-step-{id}`). */
  id: string;
  /** Step label (resolved via t inside the builder). */
  label: string;
  /** Status determining the per-step icon + tone. */
  status: ChecklistRowStatus;
  /** Optional sub-label / explanation. */
  detail?: string;
  /** Optional click handler when the step is actionable. */
  onClick?: () => void;
}

/** Bucket shape for the `filesSection()` factory. */
export interface FileBucket<TFile> {
  /** Stable id for testids (`files-bucket-{id}`). */
  id: string;
  /** Bucket label (e.g. "Wygenerowane", "Kopie"). */
  label: string;
  /** Predicate selecting the files for this bucket. */
  filter: (file: TFile) => boolean;
  /** Accept attribute for the inline upload affordance. */
  accept?: string;
  /** Upload handler — invoked with the dropped/selected files. */
  onUpload?: (files: File[]) => void;
  /** Group label resolver (file → display label inside the pill). */
  pillLabel?: (file: TFile) => string;
  /** Group tone resolver (file → pill tone). */
  pillTone?: (file: TFile) => FileListPillsItem["tone"];
  /** Click handler when a pill is clicked (review modal etc.). */
  onClick?: (file: TFile) => void;
}

// ─── activitySection ───────────────────────────────────────────────────────

export interface ActivitySectionOpts {
  /** Section key (default "activity"). */
  key?: string;
  /** tabKey override (default = key). */
  tabKey?: string;
  /** Auto-tab label override (default = title). */
  tabLabel?: (t: TFunction) => string;
  /** Title (default i18n "common.sections.activity"). */
  title?: (t: TFunction) => string;
  /** Column placement (default "side"). */
  column?: "main" | "side" | "full";
  /** Override `data-testid` on the section root. */
  testId?: string;
  /** Override page-size (default 50). */
  pageSize?: number;
  /**
   * Columns worth narrating by default (Salesforce-style tracked fields).
   * UPDATE entries touching none of them hide behind a "show all" toggle;
   * omit to show every change unfiltered.
   */
  importantFields?: readonly string[];
  /**
   * Weaves child tables' audit rows into this feed (e.g. `transaction_files`
   * verdicts and `transaction_transport` leg edits inside a transaction's
   * feed). Rows are fetched + scoped + merged chronologically with the
   * parent's own rows. Omit to keep the single-source feed (invoices, EDPR).
   *
   * Build entries with `defineAuditChildScope` so each `label` stays typed
   * against its own child row. At most `MAX_AUDIT_CHILD_SCOPES` are served.
   */
  childScopes?: readonly AnyAuditChildScope[];
}

/**
 * Activity section factory. Returns a `CustomSection<T>` that renders the
 * audit-log timeline for a given commentable/entity type. Reused by every
 * entity that exposes an audit feed.
 *
 * Per Pass G1: the factory mounts ActivityBody internally which is the SOLE
 * site that calls `useRunListQuery` for this concern. Entities pass
 * `commentableType` + the section key — they never see a hook.
 */
export function activitySection<T extends { id: number }>(
  commentableType: string,
  opts: ActivitySectionOpts = {},
): CustomSection<T> & { childScopes?: readonly AnyAuditChildScope[] } {
  const {
    key = "activity",
    tabKey,
    tabLabel,
    title,
    column = "side",
    testId = `${key}-section`,
    pageSize = 50,
    importantFields,
    childScopes,
  } = opts;

  return {
    kind: "custom",
    key,
    tabKey: tabKey ?? key,
    tabLabel,
    title: title ?? ((t) => t("common.sections.activity", "Aktywność")),
    column,
    // Surfaced on the section (not just captured by `render`) so an entity's
    // config-contract test can assert WHICH child tables feed its history.
    childScopes,
    render: (record: T) => (
      <ActivityBody
        entityType={commentableType}
        entityId={record.id}
        pageSize={pageSize}
        testId={testId}
        importantFields={importantFields}
        childScopes={childScopes}
      />
    ),
  };
}

// ─── commentsSection ───────────────────────────────────────────────────────

export interface CommentsSectionOpts {
  /** Section key (default "comments"). */
  key?: string;
  /** tabKey override (default = key). */
  tabKey?: string;
  /** Auto-tab label override (default = title). */
  tabLabel?: (t: TFunction) => string;
  /** Title (default i18n "common.sections.comments"). */
  title?: (t: TFunction) => string;
  /** Column placement (default "side"). */
  column?: "main" | "side" | "full";
  /** Override `data-testid` on the section root. */
  testId?: string;
}

/**
 * Comments section factory. Returns a `CustomSection<T>` that renders the
 * connected `<CommentThread>` for a given commentable type. The thread
 * primitive owns its own fetch — this factory is a thin wrapper that maps
 * `record.id` to `commentableId`.
 */
export function commentsSection<T extends { id: number }>(
  commentableType: string,
  opts: CommentsSectionOpts = {},
): CustomSection<T> {
  const {
    key = "comments",
    tabKey,
    tabLabel,
    title,
    column = "side",
    testId = `${key}-section`,
  } = opts;

  return {
    kind: "custom",
    key,
    tabKey: tabKey ?? key,
    tabLabel,
    title: title ?? ((t) => t("common.sections.comments", "Komentarze")),
    column,
    // CommentThread renders its own bordered section chrome (header + body),
    // so we skip the dispatcher SectionShell to avoid double cards.
    bare: true,
    render: (record: T) => (
      <CommentsBody
        commentableType={commentableType}
        commentableId={record.id}
        testId={testId}
      />
    ),
  };
}

// ─── checklistSection ──────────────────────────────────────────────────────

export interface ChecklistSectionOpts<T> {
  key?: string;
  tabKey?: string;
  title?: (t: TFunction) => string;
  column?: "main" | "side" | "full";
  /** Restrict to one page chrome — see `BaseSection.chromeOnly`. */
  chromeOnly?: "classic" | "document";
  /** Override `data-testid` on the section root. */
  testId?: string;
  /** Builds the list of checklist steps from the record. */
  steps: (record: T, t: TFunction) => ChecklistStep[];
}

/**
 * Shared checklist/status section factory. Every entity with a status state
 * machine declares its `steps(record, t)` builder; the rendering is uniform
 * (Stack of `<ChecklistRow>` rows, tone-coded by status).
 *
 * Renders inside a `Card` so it composes the same outer chrome as every
 * other section. The body intentionally bypasses `renderRecordLayout`'s
 * checklist branch because the builder form is richer than the
 * single-field `RecordLayout.kind = "checklist"` shape.
 */
export function checklistSection<T>(
  opts: ChecklistSectionOpts<T>,
): CustomSection<T> {
  const {
    key = "checklist",
    tabKey,
    title,
    column = "side",
    testId = `${key}-section`,
    chromeOnly,
    steps,
  } = opts;

  return {
    kind: "custom",
    key,
    tabKey: tabKey ?? key,
    title: title ?? ((t) => t("common.sections.checklist", "Checklist")),
    column,
    ...(chromeOnly ? { chromeOnly } : {}),
    render: (record: T, t: TFunction) => {
      const built = steps(record, t);
      return (
        <Stack gap="sm" testId={testId}>
          {built.map((step) => (
            <ChecklistRow
              key={step.id}
              id={step.id}
              label={step.label}
              status={step.status}
              detail={step.detail}
              onClick={step.onClick}
            />
          ))}
        </Stack>
      );
    },
  };
}

// ─── filesSection ──────────────────────────────────────────────────────────

/** Permanent filter a caller can pin onto the files fetch. */
export interface ExtraFilter {
  field: string;
  operator: "eq" | "in" | "ne" | "lt" | "gt" | "lte" | "gte";
  value: unknown;
}

export interface FilesSectionOpts<T, TFile> {
  key?: string;
  tabKey?: string;
  title?: (t: TFunction) => string;
  column?: "main" | "side" | "full";
  testId?: string;
  /** Schema of the files resource (e.g. `transactionFileSchema`). */
  schema: Schema<TFile>;
  /** FK column on the files table pointing to the parent record. */
  parentFk: string;
  /** Additional permanent filters (e.g. `is_latest = true`). */
  extraFilters?: ReadonlyArray<ExtraFilter>;
  /** Bucket builder (one row per bucket). */
  buckets: (record: T, t: TFunction) => FileBucket<TFile>[];
  /** Per-pill click handler — reasonable default is opening a review modal. */
  onReview?: (files: TFile[]) => void;
  /** Forwarded to `useRunListQuery.page.size`. Default 500. */
  pageSize?: number;
}

/**
 * Files section factory. Returns a `CustomSection<T>` that fetches files
 * scoped to the parent record and renders one labelled row per `FileBucket`.
 *
 * Replaces per-entity custom `<TransactionDocuments>` / `<EdprDocuments>` /
 * `<InvoiceFiles>` components that all duplicate the same fetch + group +
 * `<FileListPills>` shape. G2 entities pass a `buckets(record, t)` builder
 * — the factory owns the fetch + row layout.
 */
export function filesSection<T extends { id: number }, TFile extends { id: number | string }>(
  opts: FilesSectionOpts<T, TFile>,
): CustomSection<T> {
  const {
    key = "files",
    tabKey,
    title,
    column = "main",
    testId = `${key}-section`,
    schema,
    parentFk,
    extraFilters = [],
    buckets,
    pageSize = 500,
  } = opts;

  return {
    kind: "custom",
    key,
    tabKey: tabKey ?? key,
    title: title ?? ((t) => t("common.sections.files", "Pliki")),
    column,
    render: (record: T, t: TFunction) => (
      <FilesBody<TFile>
        schema={schema}
        parentFk={parentFk}
        parentId={record.id}
        extraFilters={extraFilters}
        buckets={buckets(record, t)}
        testId={testId}
        pageSize={pageSize}
      />
    ),
  };
}

// ─── Discriminator helper (sectionDef matching for tests) ─────────────────

/** Convenience union for tests + dispatcher narrowing. */
export type SharedSection<T> = SectionDef<T>;
